/**
 * Hook for managing company branding
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@ui/use-toast';
import { toProblem } from '@lib/api';
import {
  getCompanyBranding,
  updateCompanyBranding,
  type CompanyBranding,
  type CompanyBrandingUpdateData,
} from '@lib/api/company-branding';
import type { Problem } from '@lib/api';

export const useCompanyBranding = () => {
  const queryClient = useQueryClient();

  // Query for fetching company branding data
  const {
    data: companyBranding,
    isLoading,
    error,
    refetch,
  } = useQuery<CompanyBranding, Problem>({
    queryKey: ['companyBranding'],
    queryFn: async () => {
      try {
        return await getCompanyBranding();
      } catch (error) {
        throw toProblem(error);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Disable retries under Cypress to make error-state deterministic in tests
    retry: typeof window !== 'undefined' && 'Cypress' in window ? false : 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Mutation for updating company branding
  const {
    mutate: updateBranding,
    isPending: isSaving,
    error: saveError,
  } = useMutation<CompanyBranding, Problem, CompanyBrandingUpdateData>({
    mutationFn: updateCompanyBranding,
    onSuccess: data => {
      // Update cache with new data
      queryClient.setQueryData(['companyBranding'], data);

      // Show success message
      toast({
        title: 'Success',
        description: 'Company branding saved successfully',
      });
    },
    onError: error => {
      const problem = toProblem(error);

      // Show error message
      toast({
        title: 'Error',
        description: problem.detail || 'Failed to save company branding',
        variant: 'destructive',
      });
    },
  });

  return {
    companyBranding,
    isLoading,
    isSaving,
    error,
    saveError,
    updateCompanyBranding: updateBranding,
    refetch,
  };
};
